import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Confirm } from "semantic-ui-react";
import { useAuth } from "../../../../hooks";
import { Syllabu, Course, Week, Session } from "../../../../api";
import {
  Sidebar,
  SessionContent,
} from "../../../../components/Student/MyCourses/MediaShow";
import "./MediaShow.scss";

const syllabuController = new Syllabu();
const courseController = new Course();
const weekController = new Week();
const sessionController = new Session();

export function MediaShow() {
  const { syllabuSubjectGradeId } = useParams();
  const [weeksAndSessions, setWeeksAndSessions] = useState([]);
  const [syllabu, setSyllabu] = useState(null);
  const [subject, setSubject] = useState(null);
  const [grade, setGrade] = useState(null);
  const [reload, setReload] = useState(false);
  const [reloadWeek, setReloadWeek] = useState(true);
  const [activeWeek, setActiveWeek] = useState({});
  const [activeSession, setActiveSession] = useState({});
  const [indexActiveSlide, setIndexSlideActive] = useState(0);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const { accessToken, user } = useAuth();

  // Add new state for sidebar visibility
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check if screen is mobile size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 767);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Toggle sidebar function
  const toggleSidebar = () => {
    setSidebarVisible(!sidebarVisible);
  };

  const onReload = () => setReload((prevState) => !prevState);
  const onReloadWeek = () => setReloadWeek((prevState) => !prevState);
  const onOpenCloseConfirm = () => setShowConfirm((prevState) => !prevState);

  /**
   * Fetches and updates the list of weeks and their sessions for a given subject grade.
   * 
   * This useEffect hook is triggered when there's a need to reload the weeks and sessions data, indicated by changes to `reload`, `reloadWeek`, `syllabuSubjectGradeId`, or `accessToken`. When `reload` or `reloadWeek` is true, it performs an asynchronous operation to fetch the weeks associated with the specified `syllabuSubjectGradeId` using the provided `accessToken`. The fetched data is then used to update the state with the new weeks and sessions information.
   * 
   * If the fetch operation is successful and `reload` is true, it also triggers the `onReload` callback function to perform any additional actions required after reloading the data.
   * 
   * Errors encountered during the fetch operation are caught and logged to the console to aid in debugging and ensure the application does not crash.
   * 
   * @dependency reloadWeek - A boolean indicating if the weeks data should be reloaded due to a change in weeks.
   * @dependency reload - A boolean indicating if a general reload is requested.
   * @dependency syllabuSubjectGradeId - The ID of the subject grade for which weeks and sessions are being fetched.
   * @dependency accessToken - The access token required for authentication to perform the fetch operation.
   */
  useEffect(() => {
    (async () => {
      try {
        if (reload || reloadWeek) {
          const response = await weekController.getWeeksBySubjectForStudent(
            accessToken,
            syllabuSubjectGradeId,
            user.studentId
          );
          setWeeksAndSessions(response);
          if (reload) onReload();
        }
      } catch (error) {
        console.error(error);
      }
    })();
  }, [reloadWeek, reload, syllabuSubjectGradeId, accessToken]);

  /**
   * Fetches and updates the syllabu, subject, and grade information for a given syllabu subject grade ID.
   * 
   * This useEffect hook is triggered whenever the `syllabuSubjectGradeId` or `accessToken` changes. It performs an asynchronous operation to fetch the syllabu, subject, and grade information associated with the specified `syllabuSubjectGradeId` using the provided `accessToken`. Upon successfully fetching the data, it updates the component's state with the `syllabuId`, `subjectId`, and `gradeId` from the response.
   * 
   * If an error occurs during the fetch operation, the error is caught and logged to the console. This ensures that the application can handle the error gracefully without crashing.
   * 
   * @dependency syllabuSubjectGradeId - The ID used to fetch the syllabu, subject, and grade information.
   * @dependency accessToken - The access token required for authentication to perform the fetch operation.
   */
  useEffect(() => {
    (async () => {
      try {
        const response = await syllabuController.getSubjectGradeBySyllabu(
          accessToken,
          syllabuSubjectGradeId
        );
        setSyllabu(response.Syllabu);
        setSubject(response.subject);
        setGrade(response.Grade);
      } catch (error) {
        console.error(error);
      }
    })();
  }, [syllabuSubjectGradeId, accessToken]);

  const handleSaveAdvance = async (session, indexSlide) => {
    try {
      setIndexSlideActive(indexSlide);
      if (["CLASE"].includes(session.type) && !session.slides[indexSlide].classAdvancement) {
        // GUARDAR AVANCE DE CLASE
        const classAdvancementData = {
          studentId: user.studentId,
          slideId: session.slides[indexSlide].id,
        }
        const response = await courseController.saveClassAdvancement(accessToken, classAdvancementData);
        session.slides[indexSlide].classAdvancement = true;
      }
    } catch (error) {
      console.error(error);
    }
  }

  const handleSaveEvaluation = async (resultsData) => {
    try {
      const response = await courseController.saveEvaluation(accessToken, resultsData);
      setResults(response);
    } catch (error) {
      console.error('Error saving evaluation:', error);
    }
  };

  /**
   * Shuffles the elements of an array in place.
   * 
   * This function implements the Fisher-Yates (aka Knuth) shuffle algorithm to randomly shuffle
   * the elements of an array. It iterates over the array from the last element to the first, 
   * selecting a random element from the unshuffled portion of the array (including the current element)
   * and then swapping it with the current element. This process ensures each element has an equal
   * probability of ending up in any position within the array.
   * 
   * @param {Array} array - The array to be shuffled.
   * @returns {Array} The same array passed as input, but with its elements shuffled.
   */
  const shuffleArray = (array) => {
    let currentIndex = array.length, randomIndex;
    while (currentIndex !== 0) {
      randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex--;
      [array[currentIndex], array[randomIndex]] = [array[randomIndex], array[currentIndex]];
    }
    return array;
  }

  const handleChoiceSelection = (slideIndex, choiceIndex, answer) => {
    console.log(`Slide Index: ${slideIndex}, Choice Index: ${choiceIndex}, answer: ${answer}`);

    setActiveSession(prevSession => {
      const updatedSlides = [...prevSession.slides];
      updatedSlides[slideIndex].answer = answer;
      return { ...prevSession, slides: updatedSlides };
    });
  };

  const calculateResults = () => {
    const answers = [];
    activeSession.slides.forEach(slide => {
      if (slide.answer !== undefined) {
        answers.push({
          slideId: slide.id,
          selectedChoice: slide.answer,
        });
      }
    });
    return answers;
  };

  /**
   * Sets the active session for displaying media content and initializes the session view.
   * 
   * This asynchronous function is responsible for updating the active session state if the selected session differs from the currently active session. It performs the following steps:
   * 
   * 1. Checks if the selected session's `_id` is different from the `_id` of the currently active session. If they are the same, no further action is taken.
   * 2. Sets the loading state to true, indicating that a new session is being loaded.
   * 3. Calls `sessionController.getSessionWithSlides` with the `accessToken` and the `_id` of the selected session to fetch the session data, including its slides.
   * 4. Updates the active session state with the fetched session data.
   * 5. Sets the loading state to false, indicating that the session has been loaded.
   * 6. Resets the active slide index to 0, starting the session from the first slide.
   * 7. Calculates the initial countdown time for the first slide, if a time is specified in the slide's content. The time is expected to be in minutes and is converted to seconds. If no time is specified, or if there is an issue accessing the slide's content, `null` is set as the initial countdown time.
   * 
   * @param {Object} session - An object representing the session to be set as active, containing at least an `_id` property.
   */
  const setActiveSessionShow = async (session, lastSlide = false, resetQuestionnaire = false) => {
    if (session.id === activeSession?.id && !resetQuestionnaire) {
      return;
    }
    setShowResults(false);
    setResults(null);
    setIsLoading(true);
    const response = await sessionController.getSessionWithSlides(accessToken, session.id, user.studentId);
    if (["CUESTIONARIO", "EXAMEN"].includes(response.type)) {
      if (response.evaluations && response.evaluations.length > 0) {
        setShowResults(true);
        setResults(response.evaluations[0]);
        setIndexSlideActive(0); // Siguiente evaluación primer slide, para poder regresar al slide anterior
        setIsLoading(false);
      } else {
        setIndexSlideActive(0); // Siguiente clase primer slide
      }
      response.slides = response.slides.map(slide => {
        if (slide.content && slide.content.choices) {
          slide.content.choices = shuffleArray([...slide.content.choices]);
        }
        return slide;
      });
    } else {
      if (lastSlide) {
        handleSaveAdvance(response, response.slides.length - 1);
      } else {
        handleSaveAdvance(response, 0);
      }
    }
    await setActiveSession(response);
    const initialTime = response.slides[0]?.content?.time !== undefined
      ? parseInt(response.slides[0]?.content?.time) * 60
      : null;
    setCountdown(initialTime);
    setIsLoading(false);
  };

  /**
   * Advances the current slide to the next slide in the active session.
   * 
   * This asynchronous function checks if the current slide (`indexActiveSlide`) is not the last slide in the session's slide array (`activeSession.slides`). If there is a next slide, it calculates the new index for the next slide and retrieves the time specified for the next slide's content, if available. The time is expected to be in minutes, so it is converted to seconds by multiplying by 60.
   * 
   * After calculating the new index and the time for the next slide, it updates the state with the new slide index (`setIndexSlideActive`) and sets the countdown timer (`setCountdown`) to the time specified for the next slide, if any. If the next slide does not have a specified time, `null` is set for the countdown.
   */
  const handleNextSlide = async () => {
    if (showResults) {
      await navigateToNextSession();
      setShowResults(false);
    } else if (indexActiveSlide < activeSession?.slides?.length - 1) {
      await advanceToNextSlide();
    } else {
      if (["CUESTIONARIO", "EXAMEN"].includes(activeSession.type)) {
        const results = calculateResults();
        console.log("Active Session:", activeSession);
        console.log('Results:', results);

        // GUARDAR RESULTADOS
        const resultsData = {
          studentId: user.studentId,
          sessionId: activeSession.id,
          answers: results,
        };
        // GUARDAR RESULTADOS
        await handleSaveEvaluation(resultsData);

        setShowResults(true);
      } else {
        await navigateToNextSession();
      }
    }
  };

  const advanceToNextSlide = async () => {
    const nextIndex = indexActiveSlide + 1;
    const nextTime = activeSession.slides[nextIndex]?.content?.time !== undefined
      ? parseInt(activeSession.slides[nextIndex]?.content?.time) * 60
      : null;

    await handleSaveAdvance(activeSession, nextIndex);
    await setIndexSlideActive(nextIndex);
    await setCountdown(nextTime);
  };

  const navigateToNextSession = async () => {
    const currentWeekIndex = weeksAndSessions.findIndex(week => week.id === activeSession.weekId);
    const currentWeek = weeksAndSessions.find(week => week.id === activeSession.weekId);

    const currentSessionIndex = currentWeek?.sessions.findIndex(session => session.id === activeSession.id);

    if (currentSessionIndex < currentWeek?.sessions.length - 1) {
      const nextSession = currentWeek?.sessions[currentSessionIndex + 1];
      await setActiveSessionShow(nextSession);
    } else if (currentWeekIndex < weeksAndSessions.length - 1) {
      const nextWeek = weeksAndSessions[currentWeekIndex + 1];
      const nextSession = nextWeek.sessions[0];
      await setActiveWeek(nextWeek);
      await setActiveSessionShow(nextSession);
    }
  };

  const handleConfirmReDoTest = async () => {
    // Recargar la sesión actual para reordenar y reiniciar las selecciones
    await setActiveSessionShow(activeSession, false, true);
    setIndexSlideActive(0);
    setResults(null);
    setShowResults(false);
    onOpenCloseConfirm();
  };

  /**
   * Navigates to the previous slide in the active session.
   * 
   * This asynchronous function checks if the current active slide index (`indexActiveSlide`) is greater than 0, indicating that there is a previous slide to navigate to. If so, it calculates the new index for the previous slide and retrieves the time specified for the previous slide's content, if available. The time is expected to be in minutes, so it is converted to seconds by multiplying by 60.
   * 
   * After calculating the new index and the time for the previous slide, it updates the state with the new slide index (`setIndexSlideActive`) and sets the countdown timer (`setCountdown`) to the time specified for the previous slide, if any. If the previous slide does not have a specified time, `null` is set for the countdown.
   */
  const handlePrevSlide = async () => {
    if (indexActiveSlide > 0) {
      const newIndex = indexActiveSlide - 1;
      const beforeTime = activeSession.slides[newIndex]?.content?.time !== undefined ? parseInt(activeSession.slides[newIndex]?.content?.time) * 60 : null;
      await setIndexSlideActive(newIndex);
      await setCountdown(beforeTime);
    } else {
      // Check if there is a previous session
      const currentWeekIndex = weeksAndSessions.findIndex(week => week.id === activeSession.weekId);
      const currentWeek = weeksAndSessions.find(week => week.id === activeSession.weekId);

      // buscar la posición de la sesión actual en la unidad
      const currentSessionIndex = currentWeek?.sessions.findIndex(session => session.id === activeSession.id);

      //busca la sesión anterior si existe
      if (currentSessionIndex > 0) {
        const prevSession = currentWeek?.sessions[currentSessionIndex - 1];
        await setActiveSessionShow(prevSession, true);
      } else {
        //busca la unidad anterior si existe
        if (currentWeekIndex > 0) {
          const prevWeek = weeksAndSessions[currentWeekIndex - 1];
          const prevSession = prevWeek.sessions[prevWeek.sessions.length - 1];
          await setActiveWeek(prevWeek);
          await setActiveSessionShow(prevSession, true);
        }
      }
    }
  };

  return (
    <div className="media-show-page">
      <Sidebar
        weeks={weeksAndSessions}
        grade={grade}
        subject={subject}
        syllabu={syllabu}
        activeWeek={activeWeek}
        setActiveWeek={setActiveWeek}
        activeSession={activeSession}
        setActiveSessionShow={setActiveSessionShow}
        reload={reload}
        onReload={onReload}
        reloadWeek={reloadWeek}
        onReloadWeek={onReloadWeek}
        isMobile={isMobile}
        isVisible={sidebarVisible}
        toggleSidebar={toggleSidebar}
      />
      <SessionContent
        weeks={weeksAndSessions}
        activeWeek={activeWeek}
        activeSession={activeSession}
        indexActiveSlide={indexActiveSlide}
        countdown={countdown}
        setCountdown={setCountdown}
        handleNextSlide={handleNextSlide}
        handlePrevSlide={handlePrevSlide}
        handleChoiceSelection={handleChoiceSelection}
        results={results}
        showResults={showResults}
        isLoading={isLoading}
        onOpenCloseConfirm={onOpenCloseConfirm}
        isMobile={isMobile}
        toggleSidebar={toggleSidebar}
      />
      <Confirm
        confirmButton="Aceptar"
        cancelButton="Cancelar"
        open={showConfirm}
        onCancel={onOpenCloseConfirm}
        onConfirm={handleConfirmReDoTest}
        content="¿Estás seguro que deseas volver a realizar la sesión? Tu nuevo resultado reemplazará el anterior."
        size="mini"
      />
    </div>
  );
}
